import { ELEVENLABS_CONFIG } from '@/lib/constants';
import { STTResult } from '@/types';

/**
 * Sanitize text for TTS by removing system tags
 */
function sanitizeTextForTTS(text: string): string {
  // Remove <system-reminder>...</system-reminder> tags and their content
  const sanitized = text.replace(/<system-reminder>[\s\S]*?<\/system-reminder>/gi, '');

  // Trim any extra whitespace left after removal
  return sanitized.trim();
}

/**
 * Text-to-Speech: Convert text to audio and play it
 */
export async function textToSpeech(
  text: string,
  voiceId: string,
  apiKey: string,
  modelId?: string
): Promise<HTMLAudioElement> {
  const ttsModel = modelId || 'eleven_flash_v2_5';

  // Sanitize text before processing
  const sanitizedText = sanitizeTextForTTS(text);

  // Skip TTS if text is empty after sanitization
  if (!sanitizedText || sanitizedText.trim().length === 0) {
    console.warn('🔊 TTS: Text is empty after sanitization, skipping');
    throw new Error('Text is empty after sanitization');
  }

  // Adjust voice settings based on model
  // eleven_v3 requires stability to be 0.0, 0.5, or 1.0
  let voiceSettings: any = { ...ELEVENLABS_CONFIG.voiceSettings };
  if (ttsModel === 'eleven_v3') {
    voiceSettings = {
      stability: 1.0, // Use 1.0 (Robust) for eleven_v3 (closest to 0.9)
      similarity_boost: 0.75,
      style: 0,
      use_speaker_boost: true,
    };
  }

  try {
    console.log('🔊 TTS Request:', {
      originalLength: text.length,
      sanitizedLength: sanitizedText.length,
      text: sanitizedText.substring(0, 100) + (sanitizedText.length > 100 ? '...' : ''),
      voiceId,
      model: ttsModel,
      voiceSettings,
      language_code: ELEVENLABS_CONFIG.languageCode,
    });

    const response = await fetch(
      `${ELEVENLABS_CONFIG.baseUrl}/text-to-speech/${voiceId}`,
      {
        method: 'POST',
        headers: {
          Accept: 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': apiKey,
        },
        body: JSON.stringify({
          text: sanitizedText,
          model_id: ttsModel,
          language_code: ELEVENLABS_CONFIG.languageCode,
          voice_settings: voiceSettings,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('🔊 TTS API Error:', response.status, errorText);
      throw new Error(`TTS API error (${response.status}): ${errorText}`);
    }

    // Convert response to audio blob
    const audioBlob = await response.blob();
    console.log('🔊 TTS Response:', {
      audioSize: `${(audioBlob.size / 1024).toFixed(2)} KB`,
      type: audioBlob.type,
    });
    const audioUrl = URL.createObjectURL(audioBlob);

    // Create and return audio element
    const audio = new Audio(audioUrl);

    // Cleanup URL after audio ends
    audio.addEventListener('ended', () => {
      URL.revokeObjectURL(audioUrl);
    });

    return audio;
  } catch (error) {
    console.error('TTS error:', error);
    throw error;
  }
}

/**
 * Speech-to-Text: Convert audio blob to text transcript
 */
export async function speechToText(
  audioBlob: Blob,
  apiKey: string,
  modelId?: string
): Promise<STTResult> {
  const sttModel = modelId || 'scribe_v1';

  try {
    console.log('🎤 STT Request:', {
      audioSize: `${(audioBlob.size / 1024).toFixed(2)} KB`,
      type: audioBlob.type,
      model: sttModel,
      language: ELEVENLABS_CONFIG.language,
    });

    const formData = new FormData();
    formData.append('file', audioBlob, 'recording.webm');
    formData.append('model_id', sttModel);
    formData.append('language', ELEVENLABS_CONFIG.language);

    const response = await fetch(
      `${ELEVENLABS_CONFIG.baseUrl}/speech-to-text`,
      {
        method: 'POST',
        headers: {
          'xi-api-key': apiKey,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('🎤 STT API Error:', response.status, errorText);
      throw new Error(`STT API error (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    console.log('🎤 STT Response:', {
      transcript: result.text || '',
      confidence: result.confidence || 0.95,
      fullResponse: result,
    });

    return {
      transcript: result.text || '',
      confidence: result.confidence || 0.95,
    };
  } catch (error) {
    console.error('STT error:', error);
    throw error;
  }
}

/**
 * Queue for managing multiple TTS requests
 */
class TTSQueue {
  private queue: Array<() => Promise<void>> = [];
  private isPlaying = false;
  private currentAudio: HTMLAudioElement | null = null;

  async add(text: string, voiceId: string, apiKey: string, modelId?: string) {
    const task = async () => {
      try {
        const audio = await textToSpeech(text, voiceId, apiKey, modelId);
        this.currentAudio = audio;

        await new Promise<void>((resolve, reject) => {
          audio.onended = () => {
            this.currentAudio = null;
            resolve();
          };
          audio.onerror = (error) => {
            this.currentAudio = null;
            reject(error);
          };
          audio.play().catch(reject);
        });
      } catch (error) {
        console.error('TTS playback error:', error);
        throw error;
      }
    };

    this.queue.push(task);
    this.processQueue();
  }

  private async processQueue() {
    if (this.isPlaying || this.queue.length === 0) {
      return;
    }

    this.isPlaying = true;

    while (this.queue.length > 0) {
      const task = this.queue.shift();
      if (task) {
        try {
          await task();
        } catch (error) {
          console.error('Queue task error:', error);
        }
      }
    }

    this.isPlaying = false;
  }

  stop() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    this.queue = [];
    this.isPlaying = false;
  }

  clear() {
    this.queue = [];
  }
}

// Singleton instance
export const ttsQueue = new TTSQueue();
